"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DASHBOARD_KEY = void 0;
exports.NavBar = NavBar;
var _react = _interopRequireWildcard(require("react"));
var _reactRouterDom = require("react-router-dom");
var _reactI18next = require("react-i18next");
var _lodash = require("lodash");
var _beam = require("beam");
var _OverflowMenu = _interopRequireDefault(require("./OverflowMenu/OverflowMenu"));
var _NavButton = _interopRequireDefault(require("./NavButton"));
var _useElementWidth = require("./hooks/useElementWidth");
var _NavBarHelpers = require("./NavBarHelpers");
var _ = require("..");
var _navbar = require("../navbar");
var _clsx = _interopRequireDefault(require("clsx"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
const DEFAULT_LOGO = require('./logo.png'); // workaround typescript import error

/** The props for the NavBar component:
 * NOTE: the c1NavbarConfig is expected downstream to actually be a NavbarArgs object but for the purposes of the NavBar
 * component, we only need the c1BaseUrl and defaultRoute properties.
 * */
const useStyles = (0, _beam.makeStyles)(theme => ({
  root: {
    flex: '0 0 auto',
    zIndex: 1299 // Less than select menu (1300)
  },

  disabledLink: {
    pointerEvents: "none"
  },
  topToolbar: ({
    tenantTheme
  }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 0,
    background: tenantTheme && !tenantTheme.defaultTheme ? tenantTheme.secondaryColor : '#132354' // TODO: use palette color after theme is updated
  }),

  logoLink: ({
    tenantTheme
  }) => ({
    flex: '0 0 auto',
    width: theme.spacing(21),
    height: theme.spacing(6),
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    '&:before': {
      content: '""',
      width: theme.spacing(25.5),
      height: '100%',
      background: tenantTheme && !tenantTheme.defaultTheme ? tenantTheme.secondaryColor : '#132354',
      position: 'absolute',
      top: '0',
      left: '-36px',
      transform: 'skew(-20deg)',
      zIndex: -1
    },
    '&:link, &:visited, &:hover, &:active': {
      color: 'white',
      textDecoration: 'none'
    }
  }),
  logo: ({
    tenantTheme
  }) => ({
    backgroundImage: tenantTheme && !tenantTheme.defaultTheme ? "url('/api/rest/theme/image/logo')" : `url(${DEFAULT_LOGO})`,
    backgroundSize: tenantTheme && !tenantTheme.defaultTheme ? '100%' : '72%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    width: '100%',
    height: '100%'
  }),
  logoText: ({
    tenantTheme
  }) => ({
    color: tenantTheme && !tenantTheme.defaultTheme ? tenantTheme.navigationTextColor : theme.palette.common.white
  }),
  navigationWrapper: {
    flex: '1 1 auto',
    position: 'relative',
    marginLeft: 8,
    width: '90%',
    minWidth: 72,
    '& > *:last-child': {
      position: 'absolute',
      opacity: 0,
      top: 0,
      pointerEvents: 'none',
      marginRight: 40
    }
  },
  navigation: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    whiteSpace: 'nowrap',
    height: theme.spacing(6),
    overflow: 'visible',
    alignItems: 'flex-start',
    position: 'relative',
    zIndex: 1
  },
  overflowDetectorHidden: {
    visibility: 'hidden'
  },
  topActions: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'nowrap',
    borderLeft: '1px solid rgba(255,255,255,0.5)',
    marginRight: 24,
    fontSize: 14,
    '& > *': {
      marginLeft: '16px'
    }
  }
}));
const DASHBOARD_KEY = exports.DASHBOARD_KEY = 'dashboard';
function NavBar({
  authenticatedUser,
  theme: tenantTheme = null,
  children,
  c1NavbarConfig = {
    c1BaseUrl: '',
    defaultRoute: null
  }
}) {
  const history = (0, _reactRouterDom.useHistory)();
  const {
    t
  } = (0, _reactI18next.useTranslation)();
  const classes = useStyles({
    tenantTheme
  });
  const user = authenticatedUser?.user;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //// NavBar Width Calculations                                                                                              //
  ////   - Create a React Ref to the nav container and use that to determine whether things are overflowed                    //
  ////   - Create a React Ref to the menu's caret and use that in our calculation to  determine whether things are overflowed //
  ////   - Add a resize handler to the window and update the navWidth state and get an update on Chevron Size                 //
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  const navContainerRef = (0, _react.useRef)(0); // TODO: remove any and replace `0` with an appropriate initial value
  const overflowChevronRef = (0, _react.useRef)(0); // TODO: remove any and replace `0` with an appropriate initial value

  const navWidth = (0, _useElementWidth.useElementWidth)(navContainerRef, 0);
  const chevronWidth = (0, _useElementWidth.useElementWidth)(overflowChevronRef, 0);
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  // Menu Item Deduction and Overflow Logic                                                        //
  // - When the menu items are updated, we need to make dom refs for the overflow detector         //
  // - These dom refs will be passed to *copies* of the NavButton's which implement forwardRef     //
  // - These nav buttons will be placed underneath the navbar by the overflow detector             //
  // - We will then calculate the widths of these refed NavButtons                                 //
  //    and use them to update the overflow/visible NavBar items                                   //
  // - Whenever the navigations width or the translation change, we need to update the menu        //
  // - The itemrefs look like { menuItemKey: ref }, e.g.  { schedulesAndPlanning: ref }            //
  // The itemwidths look like { menuItemKey: width }, e.g.  { schedulesAndPlanning: 120 }          //
  ///////////////////////////////////////////////////////////////////////////////////////////////////
  const menuItems = (0, _.getNavItemsForUser)({
    user: user,
    ...c1NavbarConfig
  });
  const itemRefs = (0, _NavBarHelpers.createItemRefs)(menuItems);
  const [overflowItems, setOverflowItems] = (0, _react.useState)([]);
  const [visibleItems, setVisibleItems] = (0, _react.useState)([]);
  (0, _react.useLayoutEffect)(() => {
    const itemWidths = (0, _NavBarHelpers.getOverflowNavItemRefWidths)(itemRefs);
    const [visible, overflow] = (0, _NavBarHelpers.getShouldOverflow)(menuItems, itemWidths, navWidth, chevronWidth);
    if (!(0, _lodash.isEqual)(visible, visibleItems)) {
      setVisibleItems(visible);
    }
    if (!(0, _lodash.isEqual)(overflow, overflowItems)) {
      setOverflowItems(overflow);
    }
  }, [navWidth, t, user, authenticatedUser, chevronWidth, menuItems]);

  ///////////////////////////////////////////////////////////////////////////////////////////////////
  //// Rendering Logic                                                                             //
  //// - Only show the menu items if there are menu items to show (e.g. user is logging in)        //
  //// - The renderNavButton function is used to render both visible and overflow items            //
  ////   - Overflow items do not get a qa id but get a ref for tracking their width and vice versa //
  ///////////////////////////////////////////////////////////////////////////////////////////////////

  const showOverflow = overflowItems.length !== 0;
  const showVisible = visibleItems.length !== 0;
  const renderNavButton = (item, options = {
    withAutoQaId: true,
    ref: null
  }) => {
    const locationStrippedOfTrailingSlash = history.location.pathname.replace(/\/$/, '');
    const value = locationStrippedOfTrailingSlash || '/dashboard';
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_NavButton.default, {
      ref: options.ref,
      menuItem: item,
      url: item.url,
      external: item.external ?? false,
      isCurrent: value === item.url,
      withAutoQaId: options.withAutoQaId,
      children: item.key === DASHBOARD_KEY && /*#__PURE__*/(0, _jsxRuntime.jsx)(_beam.BeamIcon, {
        children: "Home"
      })
    }, item.key);
  };
  const renderVisibleNavButtons = visibleItems => {
    return visibleItems.map(item => renderNavButton(item));
  };
  const renderOverflowDetector = menuItems => {
    return Object.values(menuItems).map(item => {
      const options = {
        withAutoQaId: false,
        ref: itemRefs[item.key]
      };
      return renderNavButton(item, options);
    });
  };
  const canViewHome = _navbar.navbarEntries.dashboard.isAllowed({
    user,
    ...c1NavbarConfig
  });
  const {
    defaultRoute,
    externalRoute
  } = (0, _react.useMemo)(() => {
    let defaultRoute = c1NavbarConfig?.defaultRoute;
    if (!defaultRoute) {
      return {
        defaultRoute: "/dashboard",
        externalRoute: false
      };
    }
    // Here, we attempt to use the Link component to navigate to the default route if at all possible. To do this, we need
    // to know if the default route is on a different orign or path than the current location. If it is, we will use an
    // anchor, otherwise a Link component.
    // For example, if the user is on the classic site, we need to remove the /classic/# from the default route
    // so as to use Router Links as opposed to anchor tags
    const defaultRouteUrl = defaultRoute.startsWith("http") ? new URL(defaultRoute) : new URL(defaultRoute, global.location.origin);
    const externalRoute = defaultRouteUrl.origin !== global.location.origin || !(defaultRouteUrl.pathname === history.location.pathname + "/" || defaultRouteUrl.pathname === history.location.pathname);
    if (!externalRoute) {
      defaultRoute = defaultRouteUrl.hash.replace(/^#/, '');
    }
    return {
      defaultRoute,
      externalRoute
    };
  }, [c1NavbarConfig?.defaultRoute, history.location.pathname, global.location.origin]);
  const logo = (0, _react.useMemo)(() => {
    if (tenantTheme && !tenantTheme.defaultTheme && (!tenantTheme.hasLogo || !user)) {
      return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: classes.logoText,
        children: t('workforceOptimization') /* TODO: remove `any` and fix the type error */
      });
    }

    return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: classes.logo
    });
  }, [user, t, tenantTheme, classes.logo, classes.logoText]);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    className: classes.root,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)("nav", {
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_beam.Toolbar, {
        "data-testid": "NavBar-topToolbar",
        className: classes.topToolbar,
        disableGutters: true,
        children: [externalRoute ? /*#__PURE__*/(0, _jsxRuntime.jsx)("a", {
          href: defaultRoute,
          className: (0, _clsx.default)(classes.logoLink),
          "data-testid": 'NavBar-logo',
          children: logo
        }) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactRouterDom.Link, {
          className: (0, _clsx.default)(classes.logoLink, !(canViewHome || defaultRoute) && classes.disabledLink),
          to: defaultRoute,
          "data-testid": 'NavBar-logo',
          children: logo
        }), user && /*#__PURE__*/(0, _jsxRuntime.jsxs)(_react.Fragment, {
          children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
            ref: navContainerRef,
            className: classes.navigationWrapper,
            children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
              className: classes.navigation,
              children: [showVisible && renderVisibleNavButtons(visibleItems), showOverflow && /*#__PURE__*/(0, _jsxRuntime.jsx)(_OverflowMenu.default, {
                ref: overflowChevronRef,
                menuItems: overflowItems
              })]
            }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
              className: `${classes.navigation} ${classes.overflowDetectorHidden}`,
              children: renderOverflowDetector(menuItems)
            })]
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            className: classes.topActions,
            children: children
          })]
        })]
      })
    })
  });
}
//# sourceMappingURL=NavBar.js.map